import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button, Checkbox, Form, Grid, Header, Input, Menu, Modal, Segment, Tab } from "semantic-ui-react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import useReferenceService from "../../../services/backoffice/hooks/useReferenceService";
import useReferenceGroupService from "../../../services/backoffice/hooks/useReferenceGroupService";
import { CreateReferenceWithOperatingHoursCommand } from "../../../models/create-reference-with-operating-hours-command";
import { ToasterConfig } from "../../../config/ToasterConfig";
import { EDaysOfWeek, EDaysOfWeekDisplay, EDaysOfWeekFromDisplay } from "../../../models/e-days-of-week";
import { ShortDto } from "../../../models/short-dto";
import { ReferenceDto, ReferenceOperatingHourDto } from "../../../models/reference-operating-hour-dto";
import GoogleGeofenceMap from '../geofence-components/GoogleGeofenceMap';
import { GeofencePointDto } from "../../../models/requests/geofence-point-dto";
import { EGeofenceType } from "../../../models/requests/e-geofence-type";

// Interface para representar o TimeSpan do C#

interface CreateReferenceFormProps {
    isModal?: boolean;
    open?: boolean;
    onClose?: (forceClose?: boolean) => void;
    referenceId?: string;
    readOnly?: boolean;
    onFormModified?: (modified: boolean) => void;
}

const CreateReferenceForm: React.FC<CreateReferenceFormProps> = ({ 
    isModal, 
    open, 
    onClose, 
    referenceId, 
    readOnly = false,
    onFormModified 
}) => {
    const navigate = useNavigate();
    const { createReferenceWithOperatingHours, getReferenceById, updateReference } = useReferenceService();
    const { getReferenceGroupsShort } = useReferenceGroupService();
    
    // Estados do formulário
    const [referenceCode, setReferenceCode] = useState<string>('');
    const [name, setName] = useState<string>('');
    const [description, setDescription] = useState<string>('');
    const [referenceGroupId, setReferenceGroupId] = useState<string>('');
    const [isLoad, setIsLoad] = useState<boolean>(false);
    const [isUnload, setIsUnload] = useState<boolean>(false);
    const [isReferenceVisible, setIsReferenceVisible] = useState<boolean>(false);
    const [operatingHours, setOperatingHours] = useState<ReferenceOperatingHourDto[]>([]);
    const [geofencePoints, setGeofencePoints] = useState<GeofencePointDto[]>([]);
    const [referenceGroups, setReferenceGroups] = useState<ShortDto[]>([]);
    const [activeDay, setActiveDay] = useState<EDaysOfWeek>(EDaysOfWeek.Monday);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [debugInfo, setDebugInfo] = useState<string>('');
    const [configAllModalOpen, setConfigAllModalOpen] = useState<boolean>(false);
    const [allDaysStartTime, setAllDaysStartTime] = useState<string>('08:00');
    const [allDaysEndTime, setAllDaysEndTime] = useState<string>('18:00');
    const [allDaysContractedGateToGate, setAllDaysContractedGateToGate] = useState<number>(30);
    const [allDaysExpectedGateToGate, setAllDaysExpectedGateToGate] = useState<number>(20);
    const [forceUpdate, setForceUpdate] = useState<number>(0);
    
    // Função para verificar se o formulário foi modificado
    const checkFormModified = useCallback(() => {
        return (
            name.trim() !== '' ||
            referenceCode.trim() !== '' ||
            description.trim() !== '' ||
            referenceGroupId !== '' ||
            geofencePoints.length > 0 ||
            operatingHours.some(hour => 
                hour.startTime || 
                hour.endTime || 
                (hour.contractedGateToGateMinutes && hour.contractedGateToGateMinutes > 0) || 
                (hour.expectedGateToGateMinutes && hour.expectedGateToGateMinutes > 0)
            )
        );
    }, [name, referenceCode, description, referenceGroupId, geofencePoints, operatingHours]);

    // Notifica o componente pai sobre alterações não salvas
    useEffect(() => {
        if (onFormModified) {
            onFormModified(checkFormModified());
        }
    }, [onFormModified, checkFormModified]);
    const mapRef = useRef<L.Map | null>(null);

    useEffect(() => {
        getReferenceGroupsShort()
            .then((response) => {
                setReferenceGroups(response);
            })
            .catch((error) => {
                toast.error("Erro ao carregar os grupos de referência", ToasterConfig.error);
            });
    }, []);

    useEffect(() => {
        if (referenceId) {
            getReferenceById(referenceId)
                .then((response) => {
                    setReferenceCode(response.referenceCode);
                    setName(response.name);
                    setDescription(response.description);
                    setReferenceGroupId(response.referenceGroupId);
                    setIsLoad(response.isLoad);
                    setIsUnload(response.isUnload);
                    setIsReferenceVisible(response.isReferenceVisible);

                    const daysMap = new Map<number, ReferenceOperatingHourDto>();
                    response.operatingHours.forEach(item => {
                        let dayValue: number;

                        if (typeof item.dayOfWeek === 'number') {
                            dayValue = item.dayOfWeek;
                        } else if (typeof item.dayOfWeek === 'string') {
                            const dayMapping: Record<string, number> = {
                                "Sunday": 0,
                                "Monday": 1,
                                "Tuesday": 2,
                                "Wednesday": 3,
                                "Thursday": 4,
                                "Friday": 5,
                                "Saturday": 6
                            };

                            dayValue = dayMapping[item.dayOfWeek] !== undefined ? dayMapping[item.dayOfWeek] : 0;
                        } else {
                            dayValue = 0; // Valor padrão
                        }

                        const startTime = item.startTime ? formatTimeWithSeconds(item.startTime) : "";
                        const endTime = item.endTime ? formatTimeWithSeconds(item.endTime) : "";
                        daysMap.set(dayValue, {
                            id: item.id || '',
                            referenceId: item.referenceId || '',
                            dayOfWeek: dayValue,
                            startTime: startTime,
                            endTime: endTime,
                            contractedGateToGateMinutes: item.contractedGateToGateMinutes || 0,
                            expectedGateToGateMinutes: item.expectedGateToGateMinutes || 0
                        });
                    });

                    // Adicionar dias que estão faltando
                    [0, 1, 2, 3, 4, 5, 6].forEach(day => {
                        if (!daysMap.has(day)) {
                            daysMap.set(day, {
                                id: '',
                                referenceId: '',
                                dayOfWeek: day,
                                startTime: "",
                                endTime: "",
                                contractedGateToGateMinutes: 0,
                                expectedGateToGateMinutes: 0
                            });
                        }
                    });

                    // Converter o Map para array
                    const allDays = Array.from(daysMap.values());
                    setOperatingHours(allDays);

                    // Carregar pontos do geofence
                    if (response.geofence) {
                        const geofencePoints = (response.geofence?.points ?? [])
                            .slice()
                            .sort((a, b) => (a.index ?? 0) - (b.index ?? 0))
                            .map((point, index) => ({
                                ...point,
                                index: point.index ?? index,
                                id: point.id || '',
                                geofenceId: response?.geofence?.id || '',
                                isActive: point.isActive ?? true,
                                createdAt: new Date(point.createdAt)
                            }));
                        setGeofencePoints(geofencePoints); // Atualiza os pontos do geofence

                        // Centraliza o mapa nas coordenadas do geofence
                        if (geofencePoints.length > 0) {
                            const firstPoint = geofencePoints[0];
                            mapRef.current?.setView([firstPoint.latitude, firstPoint.longitude], 15);
                        }
                    }
                })
                .catch((error) => {
                    toast.error("Erro ao carregar os dados da referência", ToasterConfig.error);
                });
        } else {
            // Reset fields for new reference
            setReferenceCode('');
            setName('');
            setDescription('');
            setReferenceGroupId('');
            setIsLoad(false);
            setIsUnload(false);
            setIsReferenceVisible(false);

            // Initialize operating hours for all days of the week using numeric values
            setOperatingHours([
                { id: '', referenceId: '', dayOfWeek: 0, startTime: "", endTime: "", contractedGateToGateMinutes: 0, expectedGateToGateMinutes: 0 },
                { id: '', referenceId: '', dayOfWeek: 1, startTime: "", endTime: "", contractedGateToGateMinutes: 0, expectedGateToGateMinutes: 0 },
                { id: '', referenceId: '', dayOfWeek: 2, startTime: "", endTime: "", contractedGateToGateMinutes: 0, expectedGateToGateMinutes: 0 },
                { id: '', referenceId: '', dayOfWeek: 3, startTime: "", endTime: "", contractedGateToGateMinutes: 0, expectedGateToGateMinutes: 0 },
                { id: '', referenceId: '', dayOfWeek: 4, startTime: "", endTime: "", contractedGateToGateMinutes: 0, expectedGateToGateMinutes: 0 },
                { id: '', referenceId: '', dayOfWeek: 5, startTime: "", endTime: "", contractedGateToGateMinutes: 0, expectedGateToGateMinutes: 0 },
                { id: '', referenceId: '', dayOfWeek: 6, startTime: "", endTime: "", contractedGateToGateMinutes: 0, expectedGateToGateMinutes: 0 }
            ]);
        }
    }, [referenceId]);

    // Função para garantir que o horário está no formato correto com segundos (HH:mm:ss)
    const formatTimeWithSeconds = (timeString: string) => {
        if (!timeString) return "";
        // Se já estiver no formato HH:mm:ss, retornar como está
        if (timeString.split(":").length === 3) {
            return timeString;
        }
        // Se estiver no formato HH:mm, adicionar os segundos
        if (timeString.split(":").length === 2) {
            return `${timeString}:00`;
        }
        return timeString; // Retornar o valor original se não for reconhecido
    };

    const handleSave = async () => {
        try {
            setIsSubmitting(true);
            setDebugInfo('');
            if (!name || !referenceCode || !referenceGroupId) {
                toast.error("Preencha todos os campos obrigatórios", ToasterConfig.error);
                setIsSubmitting(false);
                return;
            }

            // Verificar se todos os dias têm horários definidos
            const diasSemHorario = operatingHours.filter(hour => !hour.startTime || !hour.endTime);
            if (diasSemHorario.length > 0) {
                const diasFaltantes = diasSemHorario.map(hour => getDayInPortuguese(hour.dayOfWeek as EDaysOfWeek)).join(", ");
                toast.error(`Defina os horários de operação para todos os dias da semana. Faltam: ${diasFaltantes}`, ToasterConfig.error);
                setIsSubmitting(false);
                return;
            }

            // Verificar se todos os dias têm valores de gate-to-gate definidos
            const diasSemGateToGate = operatingHours.filter(hour => hour.contractedGateToGateMinutes === undefined || hour.expectedGateToGateMinutes === undefined);
            if (diasSemGateToGate.length > 0) {
                const diasFaltantes = diasSemGateToGate.map(hour => getDayInPortuguese(hour.dayOfWeek as EDaysOfWeek)).join(", ");
                toast.error(`Defina os valores de gate-to-gate para todos os dias da semana. Faltam: ${diasFaltantes}`, ToasterConfig.error);
                setIsSubmitting(false);
                return;
            }

            // Filtrar horários válidos e garantir que dayOfWeek seja numérico
            const validOperatingHours = operatingHours
                .map(hour => ({
                    ...hour,
                    dayOfWeek: typeof hour.dayOfWeek === 'number' ?
                        hour.dayOfWeek :
                        EDaysOfWeekFromDisplay[hour.dayOfWeek as string] || 0
                }));

            // Remover duplicatas usando o valor numérico do dia
            const uniqueDays = new Map<number, ReferenceOperatingHourDto>();
            validOperatingHours.forEach(hour => {
                const dayValue = typeof hour.dayOfWeek === 'number' ?
                    hour.dayOfWeek :
                    EDaysOfWeekFromDisplay[hour.dayOfWeek as string] || 0;
                uniqueDays.set(dayValue, hour);
            });

            const finalOperatingHours = Array.from(uniqueDays.values());
            const createPayload: CreateReferenceWithOperatingHoursCommand = {
                reference: {
                    referenceCode: referenceCode.trim(),
                    name: name.trim(),
                    description,
                    referenceGroupId,
                    isLoad,
                    isUnload,
                    isReferenceVisible,
                    geofence: {
                        name: name,
                        type: EGeofenceType.Polygon,
                        points: geofencePoints.map((point, index) => ({
                            id: '',
                            geofenceId: '',
                            index: index,
                            latitude: point.latitude,
                            longitude: point.longitude,
                            isActive: true,
                            createdAt: new Date()
                        }))
                    }
                },
                operatingHours: operatingHours.map(hour => ({
                    referenceId: '',
                    day: hour.dayOfWeek,
                    startTime: hour.startTime,
                    endTime: hour.endTime,
                    contractedGateToGate: hour.contractedGateToGateMinutes,
                    totalGateToGate: hour.expectedGateToGateMinutes
                })),
            };

            // Chamar o serviço apropriado baseado se é criação ou atualização
            if (referenceId) {
                // Atualizar referência existente
                const updatePayload = {
                    id: referenceId,
                    referenceCode: referenceCode.trim(),
                    name: name.trim(),
                    description,
                    referenceGroupId,
                    isLoad,
                    isUnload,
                    isReferenceVisible,
                    geofence: {
                        name: name,
                        type: EGeofenceType.Polygon,
                        points: geofencePoints.map((point, index) => ({
                            id: point.id || '',
                            geofenceId: point.geofenceId || '',
                            index: index,
                            latitude: point.latitude,
                            longitude: point.longitude,
                            isActive: true,
                            createdAt: point.createdAt || new Date()
                        }))
                    },
                    operatingHours: operatingHours.map(hour => ({
                        id: hour.id || '',
                        referenceId: referenceId,
                        day: hour.dayOfWeek,
                        startTime: hour.startTime,
                        endTime: hour.endTime,
                        contractedGateToGate: hour.contractedGateToGateMinutes,
                        totalGateToGate: hour.expectedGateToGateMinutes
                    })),
                };

                await updateReference(referenceId, updatePayload);
                toast.success("Referência atualizada com sucesso!", ToasterConfig.success);
            } else {
                // Criar nova referência
                const response = await createReferenceWithOperatingHours(createPayload);
                toast.success("Referência criada com sucesso!", ToasterConfig.success);
            }

            // Notifica que não há mais alterações não salvas
            if (onFormModified) {
                onFormModified(false);
            }

            if (onClose) {
                onClose(true); // Passa true para forçar o fechamento sem confirmação
            } else {
                navigate("/torres/referencias");
            }
        } catch (error: any) {
            handleApiError(error, "processar");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleApiError = (error: any, operation: string) => {
        console.error(`Error ${operation} reference:`, error);

        // Gerar informações de diagnóstico detalhadas
        let debugDetails = `Erro ao ${operation} referência:\n`;
        debugDetails += `Status: ${error.response?.status || 'N/A'}\n`;
        debugDetails += `Mensagem: ${error.message || 'N/A'}\n`;

        if (error.response?.data) {
            debugDetails += `Dados da resposta: ${JSON.stringify(error.response.data, null, 2)}\n`;
        }

        if (error.response?.headers) {
            debugDetails += `Headers: ${JSON.stringify(error.response.headers, null, 2)}\n`;
        }

        if (error.config) {
            debugDetails += `URL: ${error.config.url || 'N/A'}\n`;
            debugDetails += `Método: ${error.config.method || 'N/A'}\n`;
            debugDetails += `Dados enviados: ${JSON.stringify(error.config.data, null, 2)}\n`;
        }

        setDebugInfo(debugDetails);

        if (error.response?.data?.errors) {
            // Exibe as mensagens de erro específicas retornadas pela API
            error.response.data.errors.forEach((err: string) => {
                toast.error(err, ToasterConfig.error);
            });
        } else if (error.response?.data?.title) {
            // Exibir título do erro se disponível
            toast.error(`Erro: ${error.response.data.title}`, ToasterConfig.error);
        } else {
            toast.error(`Erro ao ${operation} a referência. Verifique as informações de diagnóstico abaixo.`, ToasterConfig.error);
        }
    };

    const getDayInPortuguese = (day: EDaysOfWeek): string => {
        return EDaysOfWeekDisplay[day] || "Domingo";
    };

    const handleDayChange = (day: EDaysOfWeek) => {
        setActiveDay(day);
    };

    const handleTimeChange = (value: string, type: 'start' | 'end') => {
        let formattedValue = formatTimeWithSeconds(value);
        const updatedHours = [...operatingHours];
        const dayIndex = updatedHours.findIndex(hour => hour.dayOfWeek === activeDay);

        if (dayIndex !== -1) {
            updatedHours[dayIndex] = {
                ...updatedHours[dayIndex],
                [type === 'start' ? 'startTime' : 'endTime']: formattedValue
            };

        } else {
        }

        setOperatingHours(updatedHours);
    };

    const formatTimeForInput = (timeString: string) => {
        if (!timeString) return "";
        // Se já estiver no formato HH:mm:ss, converter para HH:mm
        if (timeString.includes(":")) {
            const parts = timeString.split(":");
            if (parts.length >= 2) {
                return `${parts[0]}:${parts[1]}`;
            }
        }

        return timeString; // Retornar o valor original se não for reconhecido
    };

    // Adicionando função para atualizar os valores de gate-to-gate para cada dia
    const handleGateToGateChange = (value: string, type: 'contracted' | 'expected') => {
        // Converter para número ou manter 0 se vazio
        const numericValue = value ? parseFloat(value) : 0;
        // Criar uma nova cópia do array para garantir que o React detecte a mudança
        const updatedHours = [...operatingHours];
        // Encontrar o índice do dia atual
        const dayIndex = updatedHours.findIndex(hour => hour.dayOfWeek === activeDay);
        if (dayIndex !== -1) {
            // Criar um novo objeto para o dia atual com o valor atualizado
            updatedHours[dayIndex] = {
                ...updatedHours[dayIndex],
                [type === 'contracted' ? 'contractedGateToGateMinutes' : 'expectedGateToGateMinutes']: numericValue
            };
        }

        setOperatingHours(updatedHours);
    };

    const applyConfigToAllDays = () => {
        const formattedStartTime = formatTimeWithSeconds(allDaysStartTime);
        const formattedEndTime = formatTimeWithSeconds(allDaysEndTime);
        if (operatingHours.length === 0) {
            return;
        }

        const updatedHours = operatingHours.map(hour => ({
            ...hour,
            startTime: formattedStartTime,
            endTime: formattedEndTime,
            contractedGateToGateMinutes: allDaysContractedGateToGate,
            expectedGateToGateMinutes: allDaysExpectedGateToGate
        }));

        setOperatingHours(updatedHours);
        setConfigAllModalOpen(false);
        setForceUpdate(prev => prev + 1);
        toast.success("Configuração aplicada a todos os dias da semana", ToasterConfig.success);
    };


    const renderConfigureAllModal = () => {
        return (
            <Modal
                open={configAllModalOpen}
                onClose={() => setConfigAllModalOpen(false)}
                size="large"
            >
                <Modal.Header>Configurar todos os dias</Modal.Header>
                <Modal.Content>
                    <Form>
                        <Grid>
                            <Grid.Row>
                                <Grid.Column width={8}>
                                    <Form.Field>
                                        <label>Funciona de (todos os dias)</label>
                                        <input
                                            type="time"
                                            value={allDaysStartTime}
                                            onChange={(e) => setAllDaysStartTime(e.target.value)}
                                            required
                                        />
                                    </Form.Field>
                                </Grid.Column>
                                <Grid.Column width={8}>
                                    <Form.Field>
                                        <label>Funciona até (todos os dias)</label>
                                        <input
                                            type="time"
                                            value={allDaysEndTime}
                                            onChange={(e) => setAllDaysEndTime(e.target.value)}
                                            required
                                        />
                                    </Form.Field>
                                </Grid.Column>
                            </Grid.Row>
                            <Grid.Row>
                                <Grid.Column width={8}>
                                    <Form.Field>
                                        <label>Gate-to-gate contratado (min) - todos os dias</label>
                                        <Input
                                            type="number"
                                            value={allDaysContractedGateToGate}
                                            onChange={(e) => setAllDaysContractedGateToGate(Number(e.target.value))}
                                            required
                                        />
                                    </Form.Field>
                                </Grid.Column>
                                <Grid.Column width={8}>
                                    <Form.Field>
                                        <label>Gate-to-gate esperado (min) - todos os dias</label>
                                        <Input
                                            type="number"
                                            value={allDaysExpectedGateToGate}
                                            onChange={(e) => setAllDaysExpectedGateToGate(Number(e.target.value))}
                                            required
                                        />
                                    </Form.Field>
                                </Grid.Column>
                            </Grid.Row>
                        </Grid>
                    </Form>
                </Modal.Content>
                <Modal.Actions>
                    <Button onClick={() => setConfigAllModalOpen(false)}>Cancelar</Button>
                    <Button primary onClick={applyConfigToAllDays}>Aplicar a todos os dias</Button>
                </Modal.Actions>
            </Modal>
        );
    };

    const renderDayContent = () => {
        const currentDay = operatingHours.find(hour => hour.dayOfWeek === activeDay);

        if (!currentDay) {
            console.warn(`No operating hours found for day: ${activeDay}`);
            return (
                <Grid>
                    <Grid.Row>
                        <Grid.Column width={6}>
                            <Form.Field>
                                <label>Funciona de</label>
                                <input
                                    type="time"
                                    className="ui input"
                                    style={{ width: '100%' }}
                                    value="" // Campo vazio se não houver currentDay
                                    onChange={(e) => handleTimeChange(e.target.value, 'start')}
                                    required
                                    disabled={readOnly}
                                />
                            </Form.Field>
                        </Grid.Column>
                        <Grid.Column width={6}>
                            <Form.Field>
                                <label>Funciona até</label>
                                <input
                                    type="time"
                                    className="ui input"
                                    style={{ width: '100%' }}
                                    value="" // Campo vazio se não houver currentDay
                                    onChange={(e) => handleTimeChange(e.target.value, 'end')}
                                    required
                                    disabled={readOnly}
                                />
                            </Form.Field>
                        </Grid.Column>
                    </Grid.Row>
                    <Grid.Row>
                        <Grid.Column width={6}>
                            <Form.Field>
                                <label>Gate-to-gate contratado (min)</label>
                                <Input
                                    type="number"
                                    value={0} // Valor padrão se não houver currentDay
                                    onChange={(e) => handleGateToGateChange(e.target.value, 'contracted')}
                                    required
                                    disabled={readOnly}
                                />
                            </Form.Field>
                        </Grid.Column>
                        <Grid.Column width={6}>
                            <Form.Field>
                                <label>Gate-to-gate esperado (min)</label>
                                <Input
                                    type="number"
                                    value={0} // Valor padrão se não houver currentDay
                                    onChange={(e) => handleGateToGateChange(e.target.value, 'expected')}
                                    required
                                    disabled={readOnly}
                                />
                            </Form.Field>
                        </Grid.Column>
                    </Grid.Row>
                </Grid>
            );
        }

        return (
            <Grid>
                <Grid.Row>
                    <Grid.Column width={6}>
                        <Form.Field>
                            <label>Funciona de</label>
                            <input
                                type="time"
                                className="ui input"
                                style={{ width: '100%' }}
                                value={formatTimeForInput(currentDay.startTime)}
                                onChange={(e) => handleTimeChange(e.target.value, 'start')}
                                required
                                disabled={readOnly}
                            />
                        </Form.Field>
                    </Grid.Column>
                    <Grid.Column width={6}>
                        <Form.Field>
                            <label>Funciona até</label>
                            <input
                                type="time"
                                className="ui input"
                                style={{ width: '100%' }}
                                value={formatTimeForInput(currentDay.endTime)}
                                onChange={(e) => handleTimeChange(e.target.value, 'end')}
                                required
                                disabled={readOnly}
                            />
                        </Form.Field>
                    </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                    <Grid.Column width={6}>
                        <Form.Field>
                            <label>Gate-to-gate contratado (min)</label>
                            <Input
                                type="number"
                                value={currentDay.contractedGateToGateMinutes}
                                onChange={(e) => handleGateToGateChange(e.target.value, 'contracted')}
                                required
                                disabled={readOnly}
                            />
                        </Form.Field>
                    </Grid.Column>
                    <Grid.Column width={6}>
                        <Form.Field>
                            <label>Gate-to-gate esperado (min)</label>
                            <Input
                                type="number"
                                value={currentDay.expectedGateToGateMinutes}
                                onChange={(e) => handleGateToGateChange(e.target.value, 'expected')}
                                required
                                disabled={readOnly}
                            />
                        </Form.Field>
                    </Grid.Column>
                </Grid.Row>
            </Grid>
        );
    };

    const panes = [
        {
            menuItem: 'Parâmetros',
            render: () => (
                <Tab.Pane>
                    <Form.Field>
                        <label>Código de Referência</label>
                        <Input value={referenceCode} onChange={(e) => setReferenceCode(e.target.value)} required disabled={readOnly} />
                    </Form.Field>
                    <Form.Field>
                        <label>Nome</label>
                        <Input value={name} onChange={(e) => setName(e.target.value)} required disabled={readOnly} />
                    </Form.Field>
                    <Form.Field>
                        <label>Descrição</label>
                        <Input value={description} onChange={(e) => setDescription(e.target.value)} required disabled={readOnly} />
                    </Form.Field>
                    <Form.Field>
                        <label>Grupo de Referência</label>
                        <select
                            value={referenceGroupId}
                            onChange={(e) => setReferenceGroupId(e.target.value)}
                            required
                            disabled={readOnly}
                        >
                            <option value="">Selecione um grupo</option>
                            {referenceGroups.map((group) => (
                                <option key={group.key} value={group.value}>{group.text}</option>
                            ))}
                        </select>
                    </Form.Field>
                    <Form.Field>
                        <Checkbox
                            label="Carga"
                            checked={isLoad}
                            onChange={() => setIsLoad(!isLoad)}
                            disabled={readOnly}
                        />
                    </Form.Field>
                    <Form.Field>
                        <Checkbox
                            label="Descarga"
                            checked={isUnload}
                            onChange={() => setIsUnload(!isUnload)}
                            disabled={readOnly}
                        />
                    </Form.Field>
                </Tab.Pane>
            )
        },
        {
            menuItem: 'Visibilidade',
            render: () => (
                <Tab.Pane>
                    <Segment>
                        <Header as="h4">Configurações de Visibilidade</Header>
                    <Form.Field>
                        <Checkbox
                                label="Tornar referência visível no mapa"
                            checked={isReferenceVisible}
                            onChange={() => setIsReferenceVisible(!isReferenceVisible)}
                            disabled={readOnly}
                        />
                    </Form.Field>
                    </Segment>
                </Tab.Pane>
            )
        },
        {
            menuItem: 'Premissas da Operação',
            render: () => (
                <Tab.Pane>
                    <Grid>
                        {!readOnly && (
                            <Grid.Row>
                                <Grid.Column width={16}>
                                    <Button
                                        color="blue"
                                        style={{ marginBottom: '15px' }}
                                        onClick={() => setConfigAllModalOpen(true)}
                                    >
                                        Configurar todos
                                    </Button>
                                </Grid.Column>
                            </Grid.Row>
                        )}
                        <Grid.Row>
                            <Grid.Column width={4}>
                                <Menu fluid vertical tabular>
                                    {Object.values(EDaysOfWeek).map((day) => {
                                        // Ignorar valores não numéricos do enum
                                        if (typeof day !== 'number') return null;
                                        return (
                                            <Menu.Item
                                                key={day}
                                                name={getDayInPortuguese(day)}
                                                active={activeDay === day}
                                                onClick={() => handleDayChange(day)}
                                            />
                                        );
                                    })}
                                </Menu>
                            </Grid.Column>
                            <Grid.Column stretched width={12}>
                                {renderDayContent()}
                            </Grid.Column>
                        </Grid.Row>
                    </Grid>
                </Tab.Pane>
            )
        },
        {
            menuItem: 'Configurar Cerca Eletrônica',
            render: () => (
                <Tab.Pane>
                    <GoogleGeofenceMap
                        mode="geofence"
                        readOnly={readOnly}
                        initialPoints={geofencePoints}
                        onPointsChange={(points: GeofencePointDto[]) => {
                            setGeofencePoints(points.map((point, index) => ({
                                id: '',
                                geofenceId: '',
                                index: point.index,
                                latitude: point.latitude,
                                longitude: point.longitude,
                                isActive: true,
                                createdAt: new Date()
                            })));
                        }}
                    />
                </Tab.Pane>
            )
        }
    ];

    return (
        <Segment>
            {renderConfigureAllModal()}
            <Segment color="green" style={{ marginBottom: "20px" }}>
                <Header as="h2">
                    {readOnly ? "Visualizar Referência" : (referenceId ? "Editar Referência" : "Criar Referência")}
                </Header>
            </Segment>
            <Form onSubmit={readOnly ? undefined : handleSave}>
                <Tab menu={{ pointing: true }} panes={panes} />
                {!readOnly && (
                    <div style={{ marginTop: '20px' }}>
                        <Button 
                            type="submit" 
                            color="green" 
                            loading={isSubmitting}
                            disabled={isSubmitting}
                        >
                            Salvar
                        </Button>
                        <Button 
                            type="button"
                            onClick={() => {
                                if (isModal && onClose) {
                                    onClose();
                                } else {
                                    navigate('/torres/referencias');
                                }
                            }}
                            style={{ marginLeft: '10px' }}
                            disabled={isSubmitting}
                        >
                            Cancelar
                        </Button>
                    </div>
                )}
                
                {debugInfo && (
                    <Segment secondary style={{ marginTop: '20px', overflow: 'auto', maxHeight: '300px' }}>
                        <Header as="h4">Informações de Diagnóstico</Header>
                        <pre style={{ whiteSpace: 'pre-wrap' }}>{debugInfo}</pre>
                    </Segment>
                )}
            </Form>
        </Segment>
    );
};

export default CreateReferenceForm;
